import * as React from 'react';
import { getTranslate, Translate } from 'react-localize-redux';
import { Collapse, Nav, NavItem, NavLink, TabPane } from 'reactstrap';
import { useRecoilState, useRecoilValue } from 'recoil';
import styled from 'styled-components';
import { AAdditionalColumns, getUserCategories, isAdditionalColumnTypeVisibleOnMap } from '../../../atoms/additionalColumns';
import { AAdditionalFieldColumns, isFieldTypeVisibleOnMap } from '../../../atoms/additionalFieldColumns';
import { ACalculatedFields, isPerProductCalculatedField } from '../../../atoms/calculatedFields';
import { AMapProduct, AMapProductUuid, AProducts } from '../../../atoms/product';
import { AFormTemplate } from '../../../atoms/forms';
import { AEventTypes } from '../../../atoms/global/events';
import { AFrequencyEventType } from '../../../atoms/global/frequency';
import { ASelectedFormId } from '../../../atoms/global/selectedForm';
import { useMe } from '../../../atoms/global/users';
import { Group, GroupInside, GroupInsideType } from '../../../components_v2/Selector/actions';
import storeLang from '../../../helpers/storeLang';
import { Open } from '../../../styles/global/css/Open';
import { PrimaryColor, SidelyBlack } from '../../../styles/global/css/Utils';
import { translateToString } from '../../../styles/global/translate';
import { ColumnPicker } from '../../client-companies/data/CompanyColumns';
import Restricted from '../../permissions/Restricted';
import RestrictedSuperAdmin from '../../permissions/RestrictedSuperAdmin';
import { FlexDiv } from '../../products/style';
import { FetchKey } from '../MapView';
import { ObjectAction } from '../web-types/objectActions';


type CategoryRenderType = {
	value: unknown,
	label: React.ReactNode,
	active: boolean,
	admin?: boolean
};

const CategoryTitle = styled(FlexDiv)`
	min-height: 40px;
	line-height: 1.5;
	font-weight: 500;
	cursor: pointer;
	width: 100%;
	justify-content: space-between;
	padding: 0 24px;
`;


function CategoryRender<T extends CategoryRenderType>(props: {
	categories: T[],
	name: React.ReactNode,
	onCategoryClicked: (t: T) => void,
	isOpen: boolean,
	setIsOpen: (b: boolean) => void
}): JSX.Element {
	const { categories, name, onCategoryClicked, isOpen, setIsOpen } = props;

	return <>
		<CategoryTitle onClick={() => setIsOpen(!isOpen)} color={categories.some(c => c.active) ? PrimaryColor : undefined}>
			{name}
			<Open isOpen={isOpen} size={11} />
		</CategoryTitle>
		<Collapse isOpen={isOpen}>
			{categories.map((pin, i) =>
				<RestrictedSuperAdmin key={`navItemPin[${i}]`} skip={!pin.admin}>
					<NavItem>
						<NavLink
							className={pin.active ? 'active' : ''}
							data-active={pin.active || undefined}
							onClick={() => onCategoryClicked(pin)}
						>
							{pin.label}
						</NavLink>
					</NavItem>
				</RestrictedSuperAdmin>
			)}
		</Collapse>

	</>;

}

export type PinId = 'campaign' | 'campaign_future' | 'forms' | 'status' | 'frequencies' | 'per_users' | 'urchin' | 'last' | 'next' | 'instore' | 'orders' | 'checkouts' | { additionalColumn: number } | { additionalFieldColumn: number } | { calculatedFieldColumn: number } | 'checkouts_sum';

type Pin = {
	name: string,
	objectAction?: ObjectAction,
	list: {
		value: PinId,
		label: React.ReactNode,
		active: boolean,
		admin?: boolean
	}[]
}

export default function TabPins(props: {
	handleTabRange: (t: PinId) => void
	tabType: FetchKey
}) {
	const { handleTabRange, tabType } = props;
	const translate = getTranslate(storeLang.getState().localize);
	const formTemplates = useRecoilValue(AFormTemplate).instanceForms;
	const formTemplatesList = formTemplates.map(c => ({ value: c.id, label: c.name }));
	const [selectedFormId, setSelectedFormId] = useRecoilState(ASelectedFormId);
	const eventTypes = useRecoilValue(AEventTypes);
	const [selectedFrequencyEventType, setSelectedFrequencyEventType] = useRecoilState(AFrequencyEventType);
	const additionalColumns = useRecoilValue(AAdditionalColumns);
	const eventTypeList = eventTypes.map(c => ({ value: c, label: translate(`event.${c.name}`).toString() }));
	const additionalFieldColumns = useRecoilValue(AAdditionalFieldColumns);
	const calculatedFields = useRecoilValue(ACalculatedFields);
	const products = useRecoilValue(AProducts);
	const mapProduct = useRecoilValue(AMapProduct);
	const [, setMapProductUuid] = useRecoilState(AMapProductUuid);
	const productList = React.useMemo(() => [...products]
		.sort((a, b) => a.name.toLocaleLowerCase().localeCompare(b.name.toLocaleLowerCase()))
		.map(p => ({ value: p, label: p.name })), [products]);
	const me = useMe();
	const [userCategories, setUserCategories] = React.useState<Group[]>([]);
	const enrichedUserCategories: (Group & { inside: (GroupInside & { name: string })[] })[] = React.useMemo(() => {
		return userCategories.map((category: Group & { inside: (GroupInside & { name: string })[] }) => {
			const insideWithName = category.inside.reduce((acc, inside: GroupInside) => {
				// unticked in "Champs visibles sur la fiche société": no pin layer either
				if (inside.ishidded) return acc;
				const additionalColumn = additionalColumns.find(c => c.id === inside.id);
				const et = additionalFieldColumns?.find(et => et.field_id === inside.id);
				const cf = calculatedFields.find(c => c.id === inside.id);
				const name = additionalColumn?.name ?? cf?.field_name ?? et?.field_name ?? '';
				const keyType = matchUserCategoryType(inside.type_);

				let visible = false;
				if (keyType === 'additionalColumn') {
					visible = additionalColumn && isAdditionalColumnTypeVisibleOnMap(additionalColumn.type) ?? false;
				} else if (keyType === 'additionalFieldColumn') {
					visible = et && isFieldTypeVisibleOnMap(et.field_type) ?? false;
				} else if (keyType === 'calculatedFieldColumn') {
					visible = true;
				}

				if (visible) {
					acc.push({ ...inside, name });
				}
				return acc;
			}, [] as (GroupInside & { name: string })[]);

			insideWithName.sort((a, b) => a.name.toLocaleLowerCase().localeCompare(b.name.toLocaleLowerCase()));
			category.inside = insideWithName;
			return category;
		});
	}, [userCategories, additionalColumns, calculatedFields, additionalFieldColumns]);

	function matchUserCategoryType(groupInsideType: GroupInsideType): 'additionalColumn' | 'calculatedFieldColumn' | 'additionalFieldColumn' | undefined {
		switch (groupInsideType) {
			case 'AdditionalColumn': return 'additionalColumn';
			case 'CalculatedField': return 'calculatedFieldColumn';
			case 'FormField': return 'additionalFieldColumn';
		}
	}

	const pinsList: Pin[] = [
		{
			name: translate('Company Details').toString(),
			list: [
				{ value: 'status', label: <Translate id="status" />, active: tabType === 'status' },
				{ value: 'per_users', label: <Translate id="User" />, active: tabType === 'per_users' },
				{ value: 'urchin', label: <Translate id="urchin" />, active: tabType === 'urchin' }
			]
		},
		{
			name: translateToString('general.campaigns'),
			objectAction: 'ReadCampaign',
			list: [
				{ value: 'campaign', label: <Translate id="current_campagnes" />, active: tabType === 'campaign' },
				{ value: 'campaign_future', label: <Translate id="future_campagnes" />, active: tabType === 'campaign_future' }
			]
		},
		{
			name: translate('events').toString(),
			objectAction: 'ReadEvent',
			list: [
				{ value: 'last', label: <Translate id="Last completed event" />, active: tabType === 'last' },
				{ value: 'next', label: <Translate id="Next planned event" />, active: tabType === 'next' },
				{
					value: 'frequencies', label: <FlexDiv gap='5px'>
						<Translate id="frequency" />
						<ColumnPicker
							datalist={eventTypeList}
							columnId={''}
							selectedValue={eventTypeList.find(et => et.value.id === selectedFrequencyEventType)}
							onChange={value => setSelectedFrequencyEventType(value.value.id)}
							height='12px'
						/>
					</FlexDiv>, active: tabType === 'frequencies'
				}
			]
		},
		{
			name: translate('forms').toString(),
			objectAction: 'ReadForm',
			list: [
				{
					value: 'forms', label:
						<FlexDiv gap='5px'>
							<Translate id="last" />
							<ColumnPicker
								datalist={formTemplatesList}
								columnId={''}
								selectedValue={formTemplatesList.find(ft => ft.value === selectedFormId)}
								onChange={value => setSelectedFormId(value.value)}
								height='12px'
							/>
						</FlexDiv>
					, active: tabType === 'forms'
				}
			]
		},
		...enrichedUserCategories.map(c => ({
			name: c.name === 'default_company_fields' ? translateToString('default_company_fields') : c.name,
			objectAction: 'ReadForm',
			list: c.inside.map((item: GroupInside & { name: string }) => {
				const keyType = matchUserCategoryType(item.type_);
				const calculatedField = keyType === 'calculatedFieldColumn' ? calculatedFields.find(cf => cf.id === item.id) : undefined;
				return {
					value: { [keyType!]: item.id } as PinId,
					label: calculatedField && isPerProductCalculatedField(calculatedField)
						? <FlexDiv gap='5px'>
							{item.name}
							<ColumnPicker
								datalist={productList}
								columnId={''}
								selectedValue={productList.find(p => p.value.uuid === mapProduct?.uuid)}
								onChange={value => setMapProductUuid(value.value.uuid)}
								height='12px'
							/>
						</FlexDiv>
						: item.name,
					active: (keyType && typeof tabType != 'string' && keyType in tabType && tabType[keyType] === item.id) ?? false,
				};
			})
		})).filter(i => i.list.length > 0)
	];

	const [pinOpen, setPinOpen] = React.useState<string | undefined>(pinsList.find(p => p.list.some(p => p.active))?.name);

	React.useEffect(() => {
		getUserCategories(me.id).then(data => {
			setUserCategories(data);
		});
	}, []);

	React.useEffect(() => {
		setPinOpen(pinsList.find(p => p.list.some(p => p.active))?.name);
	}, [tabType]);

	return (
		<TabPane tabId={1} style={{ height: '100%' }}>
			<div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
				<h5 style={{ color: SidelyBlack, fontSize: 13, fontWeight: 'bold', padding: '1rem 1rem 0.6rem 1rem' }}>
					<Translate id="Pin representation" />
				</h5>
				<div style={{ overflow: 'auto', flexShrink: 1, height: '100%' }}>
					<Nav tabs vertical className="nav-pin">
						{pinsList.map((pl, i) =>
							<Restricted to={pl.objectAction ? { objectAction: pl.objectAction } : []} key={`PinCategory[${i}]`}>
								<CategoryRender
									categories={pl.list}
									name={pl.name}
									onCategoryClicked={pin => handleTabRange(pin.value)}
									isOpen={pinOpen === pl.name}
									setIsOpen={b => setPinOpen(b ? pl.name : undefined)}
								/>
							</Restricted>
						)}
					</Nav>
				</div>
			</div>
		</TabPane>
	);
}
